import {ChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange/changeableFields'

import type {FieldButtonProps} from '../types/fieldConfiguration'

/**
 * Changeable field state from tuc-processes
 */
export interface ChangeableFieldState {
  state: ChangeableField['state']
  isDirty: boolean
}

/**
 * FieldButton state props, always fully resolved (no optional members)
 */
export type FieldButtonStateProps = Required<
  Pick<FieldButtonProps, 'isReadOnly' | 'isChanged' | 'requiresApproval'>
>

/**
 * Derives FieldButton state props from a single changeable field result.
 * Maps the changeable field system states to the FieldButton component props.
 *
 * @param changeableField - The changeable field state from useChangeableField hook
 * @returns Props for FieldButton: isReadOnly, isChanged, requiresApproval
 *
 * @example
 * const changeableField = useChangeableField(fieldName)
 * const stateProps = getFieldButtonStateProps(changeableField)
 * // { isReadOnly: false, isChanged: true, requiresApproval: false }
 */
export const getFieldButtonStateProps = (
  changeableField: ChangeableFieldState
): FieldButtonStateProps => ({
  isReadOnly: changeableField.state === 'notChangeable',
  isChanged: changeableField.isDirty,
  requiresApproval:
    changeableField.isDirty && changeableField.state === 'requireDispatchConfirmation'
})

/**
 * Combines multiple changeable field states into a single FieldButton state.
 * Used for composite fields like intervals that represent multiple form fields.
 *
 * Combination logic:
 * - isReadOnly: true only if ALL fields are not changeable
 * - isChanged: true if ANY field is dirty
 * - requiresApproval: true if ANY dirty field requires dispatch confirmation
 *
 * @param changeableFields - Array of changeable field states
 * @returns Combined props for FieldButton: isReadOnly, isChanged, requiresApproval
 *
 * @example
 * const spacingField = useChangeableField({field: 'spacingOrFlowRate'})
 * const timeField = useChangeableField({field: 'timeOnJobSite'})
 * const stateProps = getCombinedFieldButtonStateProps([spacingField, timeField])
 */
export const getCombinedFieldButtonStateProps = (
  changeableFields: ChangeableFieldState[]
): FieldButtonStateProps => {
  // isReadOnly: all fields must be not changeable
  const isReadOnly = changeableFields.every((field) => field.state === 'notChangeable')

  // isChanged: any field is dirty
  const isChanged = changeableFields.some((field) => field.isDirty)

  // requiresApproval: any dirty field requires dispatch confirmation
  const requiresApproval = changeableFields.some(
    (field) => field.isDirty && field.state === 'requireDispatchConfirmation'
  )

  return {isReadOnly, isChanged, requiresApproval}
}
